import React, { FormEvent, useState } from "react";
import { useDispatch } from "react-redux";
import { setAdvancedInvestmentSearchParams } from "../../../../redux/actions/investments";
import { hideModal } from "../../../../redux/actions/modal";
import { IInvestmentSearchParams } from "../../../../interfaces/investment";
import CustomSelectDropdown from "../../../../components/CustomHTMLElements/CustomSelectDropdown";
import useInvestmentCurrencies from "../../../../custom-hooks/useInvestmentCurrencies";
import useGetInvestmentStatus from "../../../../custom-hooks/useGetInvestmentStatus";
import useGetDateSearchMode from "../../../../custom-hooks/useGetDateSearchMode";
import useLegalEntities from "../hooks/useLegalEntities";
import useRelationshipManagers from "../hooks/useRelationshipManagers";

interface IProps {
  id: number;
  name: string;
  description: string;
}

const AdvancedInvestmentSearchModal = ({
  onCloseModal,
}: {
  onCloseModal: () => void;
}) => {
  const [state, setState] = useState<IInvestmentSearchParams>({
    currency: "",
    searchValue: "",
    aggregatorId: "",
    dateSearchMode: 0,
    investmentStatus: 0,
    legalEntity: "",
    relationshipManagerId: "",
  });

  const dispatch = useDispatch();

  const { data: investmentCurrencies } = useInvestmentCurrencies();
  const { data: investmentStatus } = useGetInvestmentStatus();
  const { data: dateMode } = useGetDateSearchMode();
  const { data: legalEntities } = useLegalEntities();
  const { data: relationshipManagers } = useRelationshipManagers();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    dispatch(setAdvancedInvestmentSearchParams(state));
    dispatch(hideModal());
  };

  const handleClear = () => {
    const clearedState: IInvestmentSearchParams = {
      currency: "",
      searchValue: "",
      aggregatorId: "",
      dateSearchMode: 0,
      investmentStatus: 0,
      legalEntity: "",
      relationshipManagerId: "",
    };
    setState(clearedState);
    dispatch(setAdvancedInvestmentSearchParams(clearedState));
    onCloseModal();
  };

  return (
    <div
      className="Modal__dialog lg-modal animated slideInDown"
      role="document"
    >
      <div className="LoanModal Modal__content">
        <div className="Modal__body" style={{ overflow: "hidden" }}>
          <React.Fragment>
            <h3 className="Modal__title p-b-1">Advanced Search:</h3>
            <div id="investment-search">
              <div className="m-b-30">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-4 col-sm-12 mt-2">
                      <label htmlFor="searchValue">Funder Name:</label>
                      <input
                        type="text"
                        className="form-control"
                        onChange={handleChange}
                        name="searchValue"
                        value={state.searchValue}
                        placeholder="Enter funder name..."
                      />
                    </div>
                    <div className="col-md-4 col-sm-12 mt-2">
                      <label htmlFor="currency">Currency:</label>
                      <CustomSelectDropdown
                        onChange={handleChange}
                        name="currency"
                        value={state.currency}
                        isShowDropdownArrow
                      >
                        <option value="">All Currencies</option>
                        {investmentCurrencies &&
                          investmentCurrencies?.data.map(
                            (_currency: any, index: number) => (
                              <option
                                key={index}
                                value={_currency?.currency_code}
                              >
                                {_currency?.currency_code}
                              </option>
                            ),
                          )}
                      </CustomSelectDropdown>
                    </div>
                    <div className="col-md-4 col-sm-12 mt-2">
                      <label htmlFor="investmentStatus">Status:</label>
                      <CustomSelectDropdown
                        onChange={handleChange}
                        name="investmentStatus"
                        value={state.investmentStatus}
                        isShowDropdownArrow
                      >
                        <option value="">Select Status</option>
                        {investmentStatus &&
                          investmentStatus?.data?.map((status: IProps) => (
                            <option value={status.id} key={status.description}>
                              {status.description}
                            </option>
                          ))}
                      </CustomSelectDropdown>
                    </div>
                    <div className="col-md-4 col-sm-12 mt-2">
                      <label htmlFor="dateSearchMode">Date Mode:</label>
                      <CustomSelectDropdown
                        onChange={handleChange}
                        name="dateSearchMode"
                        value={state.dateSearchMode}
                        isShowDropdownArrow
                      >
                        <option value="">Select Date Mode</option>
                        {dateMode &&
                          dateMode?.data?.map((mode: IProps) => (
                            <option value={mode.id} key={mode.description}>
                              {mode.description}
                            </option>
                          ))}
                      </CustomSelectDropdown>
                    </div>
                    <div className="col-md-4 col-sm-12 mt-2">
                      <label htmlFor="legalEntity">Legal Entity:</label>
                      <CustomSelectDropdown
                        onChange={handleChange}
                        name="legalEntity"
                        value={state.legalEntity}
                        isShowDropdownArrow
                      >
                        <option value="">Select Legal Entity</option>
                        {legalEntities &&
                          legalEntities?.data?.map((entity) => (
                            <option value={entity.id} key={entity.id}>
                              {entity.name}
                            </option>
                          ))}
                      </CustomSelectDropdown>
                    </div>
                    <div className="col-md-4 col-sm-12 mt-2">
                      <label htmlFor="relationshipManagerId">
                        Relationship Manager:
                      </label>
                      <CustomSelectDropdown
                        onChange={handleChange}
                        name="relationshipManagerId"
                        value={state.relationshipManagerId}
                        isShowDropdownArrow
                      >
                        <option value="">Select Relationship Manager</option>
                        {relationshipManagers &&
                          relationshipManagers?.data.map((manager) => (
                            <option value={manager.id} key={manager.id}>
                              {`${manager?.firstName} ${manager?.lastName}`}
                            </option>
                          ))}
                      </CustomSelectDropdown>
                    </div>
                  </div>
                  <div className="Form__action-buttons m-t-2">
                    <button
                      type="submit"
                      className="btn zoom-element btn login-btn m-0 mr-auto"
                    >
                      <i className="fa fa-check-circle" /> Search
                    </button>
                    <button
                      type="button"
                      onClick={handleClear}
                      className="btn zoom-element btn login-btn m-0 ml-auto"
                    >
                      <i className="fa fa-times-circle" /> Clear
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </React.Fragment>
        </div>
      </div>
    </div>
  );
};

export default AdvancedInvestmentSearchModal;
